/** @format */

/**
 * LangGraph 服务拓扑：从 Span Attributes（langgraph_step / triggers 等）推断连边与布局。
 */

import type { TraceAnalysisDetailSpan } from '../../typescript'
import {
  buildLangGraphEdges,
  buildLangGraphParentMap,
  createLangGraphContext,
  getNoStepSpanLayers,
  splitStepSpans,
  sortSpanSiblings
} from '../langgraphSpanTree'
import type { LangGraphContext, LangGraphSpanEdge } from '../langgraphSpanTree'
import type { TopologyLayoutDirection, TopologyLayoutOptions } from './types'

export type { LangGraphSpanEdge }

export interface LangGraphTopologyLayoutOptions extends TopologyLayoutOptions {
  /** 未指定时根据 step 层数与同层节点数自动判断 */
  direction?: TopologyLayoutDirection
}

export interface LangGraphSpanTopology {
  edges: LangGraphSpanEdge[]
  positions: Map<string, { x: number; y: number }>
  layoutDirection: TopologyLayoutDirection
}

function placeRowCentered(
  rowSpans: TraceAnalysisDetailSpan[],
  y: number,
  positions: Map<string, { x: number; y: number }>,
  nodeWidth: number,
  gapX: number
) {
  const sorted = sortSpanSiblings(rowSpans, true)
  if (!sorted.length) return

  const totalWidth = sorted.length * nodeWidth + (sorted.length - 1) * gapX
  const startX = -totalWidth / 2
  sorted.forEach((span, index) => {
    positions.set(span.SpanId, { x: startX + index * (nodeWidth + gapX), y })
  })
}

function placeColumnCentered(
  colSpans: TraceAnalysisDetailSpan[],
  x: number,
  positions: Map<string, { x: number; y: number }>,
  nodeHeight: number,
  gapY: number
) {
  const sorted = sortSpanSiblings(colSpans, true)
  if (!sorted.length) return

  const totalHeight = sorted.length * nodeHeight + (sorted.length - 1) * gapY
  const startY = -totalHeight / 2
  sorted.forEach((span, index) => {
    positions.set(span.SpanId, { x, y: startY + index * (nodeHeight + gapY) })
  })
}

/** 同层节点数 > 层级数时水平展开，否则垂直展开 */
export function decideLangGraphLayoutDirection(ctx: LangGraphContext): TopologyLayoutDirection {
  const noStepLayers = getNoStepSpanLayers(ctx)
  let layerCount = noStepLayers.length
  let maxBreadth = noStepLayers.reduce((max, layer) => Math.max(max, layer.length), 0)

  for (const step of ctx.sortedSteps) {
    const { level, nested } = splitStepSpans(ctx, step)
    layerCount += 1
    maxBreadth = Math.max(maxBreadth, level.length)
    if (nested.length) {
      layerCount += 1
      maxBreadth = Math.max(maxBreadth, nested.length)
    }
  }

  return maxBreadth > layerCount ? 'horizontal' : 'vertical'
}

function normalizePositions(positions: Map<string, { x: number; y: number }>, axis: 'x' | 'y') {
  let min = Infinity
  positions.forEach(pos => {
    min = Math.min(min, pos[axis])
  })
  if (min < 0) {
    positions.forEach((pos, spanId) => {
      positions.set(spanId, { ...pos, [axis]: pos[axis] - min })
    })
  }
}

function resolveNestedParentId(
  span: TraceAnalysisDetailSpan,
  parentMap: Map<string, string>
): string | undefined {
  return parentMap.get(span.SpanId) ?? span.ParentSpanId ?? undefined
}

function buildLangGraphVerticalLayout(
  ctx: LangGraphContext,
  nodeWidth: number,
  nodeHeight: number,
  gapX: number,
  gapY: number,
  parentMap: Map<string, string>
): Map<string, { x: number; y: number }> {
  const rowHeight = nodeHeight + gapY
  const positions = new Map<string, { x: number; y: number }>()
  let y = 0

  for (const layer of getNoStepSpanLayers(ctx)) {
    placeRowCentered(layer, y, positions, nodeWidth, gapX)
    y += rowHeight
  }

  for (const step of ctx.sortedSteps) {
    const { level, nested } = splitStepSpans(ctx, step)
    placeRowCentered(level, y, positions, nodeWidth, gapX)
    y += rowHeight

    if (nested.length) {
      const nestedY = y
      sortSpanSiblings(nested, true).forEach(child => {
        const parentId = resolveNestedParentId(child, parentMap)
        const parentX = parentId ? positions.get(parentId)?.x : undefined
        positions.set(child.SpanId, { x: parentX ?? 0, y: nestedY })
      })
      y += rowHeight
    }
  }

  normalizePositions(positions, 'x')
  return positions
}

function buildLangGraphHorizontalLayout(
  ctx: LangGraphContext,
  nodeWidth: number,
  nodeHeight: number,
  gapX: number,
  gapY: number,
  parentMap: Map<string, string>
): Map<string, { x: number; y: number }> {
  const layerGap = gapX + 24
  const siblingGap = gapY + 8
  const colWidth = nodeWidth + layerGap
  const positions = new Map<string, { x: number; y: number }>()
  let x = 0

  for (const layer of getNoStepSpanLayers(ctx)) {
    placeColumnCentered(layer, x, positions, nodeHeight, siblingGap)
    x += colWidth
  }

  for (const step of ctx.sortedSteps) {
    const { level, nested } = splitStepSpans(ctx, step)
    placeColumnCentered(level, x, positions, nodeHeight, siblingGap)
    x += colWidth

    if (nested.length) {
      const nestedX = x
      sortSpanSiblings(nested, true).forEach(child => {
        const parentId = resolveNestedParentId(child, parentMap)
        const parentY = parentId ? positions.get(parentId)?.y : undefined
        positions.set(child.SpanId, { x: nestedX, y: parentY ?? 0 })
      })
      x += colWidth
    }
  }

  normalizePositions(positions, 'y')
  return positions
}

function buildLangGraphLayoutFromContext(
  ctx: LangGraphContext,
  parentMap: Map<string, string>,
  options: LangGraphTopologyLayoutOptions = {}
): { positions: Map<string, { x: number; y: number }>; layoutDirection: TopologyLayoutDirection } {
  const nodeWidth = options.nodeWidth ?? 168
  const nodeHeight = options.nodeHeight ?? 60
  const gapX = options.gapX ?? 28
  const gapY = options.gapY ?? 40
  const layoutDirection = options.direction ?? decideLangGraphLayoutDirection(ctx)
  const positions =
    layoutDirection === 'horizontal'
      ? buildLangGraphHorizontalLayout(ctx, nodeWidth, nodeHeight, gapX, gapY, parentMap)
      : buildLangGraphVerticalLayout(ctx, nodeWidth, nodeHeight, gapX, gapY, parentMap)

  return { positions, layoutDirection }
}

/** step0 → step1 → … 自上而下，每层水平居中；同 step 嵌套子节点对齐父节点 */
export function buildLangGraphTopologyLayout(
  spans: TraceAnalysisDetailSpan[],
  options: LangGraphTopologyLayoutOptions = {}
): Map<string, { x: number; y: number }> {
  if (!spans.length) return new Map()
  const ctx = createLangGraphContext(spans)
  const parentMap = buildLangGraphParentMap(spans)
  return buildLangGraphLayoutFromContext(ctx, parentMap, options).positions
}

/** 拓扑图一次性产出连边、坐标与布局方向 */
export function buildLangGraphSpanTopology(
  spans: TraceAnalysisDetailSpan[],
  layoutOptions?: LangGraphTopologyLayoutOptions
): LangGraphSpanTopology {
  const ctx = createLangGraphContext(spans)
  const parentMap = buildLangGraphParentMap(spans)
  const { positions, layoutDirection } = buildLangGraphLayoutFromContext(
    ctx,
    parentMap,
    layoutOptions
  )
  return {
    edges: buildLangGraphEdges(spans),
    positions,
    layoutDirection
  }
}

/** @deprecated 使用 buildLangGraphSpanTopology */
export const buildLangGraphTopology = buildLangGraphSpanTopology

export type LangGraphTopology = LangGraphSpanTopology
